/**
 * Оболочка кабинета: боковая навигация на широком экране, нижняя — на телефоне
 * (ТЗ §10.5). Шапка закреплена — единственное место, где допустимо размытие.
 */
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import {
  BarChart3,
  BookOpen,
  ClipboardList,
  ShieldAlert,
  GraduationCap,
  LayoutDashboard,
  Layers,
  ListChecks,
  LogOut,
  Settings,
  UserCheck,
  Users,
} from 'lucide-react'

import { useAuth } from '@/features/auth/useAuth'
import { BannerStrip } from '@/features/site/BannerStrip'
import { ROLE_LABELS, fullName } from '@/entities/user/types'
import { Button } from '@/shared/ui/Button'
import { ThemeToggle } from '@/shared/ui/ThemeToggle'
import { cn } from '@/shared/lib/cn'

/** Банк заданий доступен только учителю: там лежат правильные ответы. */
const NAV: Array<{
  to: string
  label: string
  Icon: typeof LayoutDashboard
  teacherOnly?: boolean
  studentOnly?: boolean
  adminOnly?: boolean
}> = [
  { to: '/кабинет', label: 'Обзор', Icon: LayoutDashboard, teacherOnly: false },
  { to: '/работы', label: 'Работы', Icon: ClipboardList, teacherOnly: false },
  { to: '/классы', label: 'Классы', Icon: Users, teacherOnly: false },
  { to: '/теория', label: 'Теория', Icon: BookOpen, teacherOnly: false },
  { to: '/задания', label: 'Задания', Icon: ListChecks, teacherOnly: true },
  { to: '/наборы', label: 'Наборы', Icon: Layers, teacherOnly: true },
  { to: '/проверка', label: 'Проверка', Icon: UserCheck, teacherOnly: true },
  { to: '/результаты', label: 'Результаты', Icon: BarChart3, studentOnly: true },
  { to: '/настройки', label: 'Настройки', Icon: Settings, teacherOnly: false },
  { to: '/админ', label: 'Админ', Icon: ShieldAlert, adminOnly: true },
]

/** На телефоне в нижнюю панель влезает немного — остальное живёт в «Обзоре». */
const MOBILE_NAV = [
  '/кабинет',
  '/работы',
  '/классы',
  '/теория',
  '/проверка',
  '/результаты',
]

export function AppLayout() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  if (!user) return null

  const isTeacher = user.role === 'teacher' || user.role === 'admin'
  // «Результаты» — свой экран ученика, учителю он не нужен: у него есть
  // статистика класса и карточки учеников.
  const isAdmin = user.role === 'admin'
  const nav = NAV.filter(
    (item) =>
      (!item.teacherOnly || isTeacher) &&
      (!item.studentOnly || !isTeacher) &&
      (!item.adminOnly || isAdmin),
  )
  const mobileNav = nav.filter((item) => MOBILE_NAV.includes(item.to))

  const handleLogout = async () => {
    await logout()
    navigate('/', { replace: true })
  }

  return (
    <div className="min-h-dvh bg-bg">
      <header className="sticky top-0 z-20 border-b border-line bg-nav/85 backdrop-blur">
        <div className="shell flex h-16 items-center justify-between gap-4">
          <NavLink to="/кабинет" className="flex items-center gap-2 font-semibold text-ink">
            <GraduationCap aria-hidden className="size-5 text-accent" />
            Учусь тут
          </NavLink>
          <div className="flex items-center gap-3">
            <div className="hidden text-right sm:block">
              <div className="text-meta font-medium text-ink">{fullName(user)}</div>
              <div className="text-label text-ink-muted">{ROLE_LABELS[user.role]}</div>
            </div>
            <ThemeToggle />
            <Button variant="ghost" size="sm" icon={<LogOut className="size-4" />} onClick={handleLogout}>
              <span className="hidden sm:inline">Выйти</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="shell flex gap-8 py-6 pb-24 md:pb-6">
        <nav aria-label="Разделы" className="hidden w-52 shrink-0 md:block">
          <ul className="flex flex-col gap-1">
            {nav.map(({ to, label, Icon }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  className={({ isActive }) =>
                    cn(
                      'flex min-h-11 items-center gap-2.5 rounded-md px-3 text-meta',
                      'transition-colors duration-DEFAULT',
                      isActive
                        ? 'bg-raised font-medium text-ink'
                        : 'text-ink-soft hover:bg-raised hover:text-ink',
                    )
                  }
                >
                  <Icon aria-hidden className="size-4" />
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <main className="min-w-0 flex-1">
          <div className="mb-4">
            <BannerStrip placement="cabinet" />
          </div>
          <Outlet />
        </main>
      </div>

      {/* Нижняя навигация для экранов от 360px */}
      <nav
        aria-label="Разделы"
        className="fixed inset-x-0 bottom-0 z-20 border-t border-line bg-nav md:hidden"
      >
        <ul className="flex">
          {mobileNav.map(({ to, label, Icon }) => (
            <li key={to} className="flex-1">
              <NavLink
                to={to}
                className={({ isActive }) =>
                  cn(
                    'flex min-h-14 flex-col items-center justify-center gap-1 text-label',
                    isActive ? 'text-accent' : 'text-ink-muted',
                  )
                }
              >
                <Icon aria-hidden className="size-5" />
                {label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  )
}
